import React, { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import styled from "styled-components";
import {
    Checkbox,
    FormControlLabel,
    Icon,
    MenuItem
} from "@material-ui/core";
import _ from "lodash";

import {
    ActionPermissionsObject,
    GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS,
    IWorkspacePermissionAccess,
    IWorkspaceResourceFolder,
    IWorkspaceUserGroup,
    IWorkspaceUserExtended,
    isAllTeamMembersFolder,
    WorkspaceUserGroupType,
    WorkspacePermissionSubjectType,
    WorkspaceResourceFolderType,
    WorkspaceUserRole
} from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import { formatter } from "common/formatter";
import { Button as BeautifulButton } from "js/Components/Button";
import WorkspaceController, {
    WorkspaceControllerState,
    getUserPermissionsToResourceFolder
} from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { auth } from "js/firebase/auth";
import { t } from "js/i18n";
import {
    ShowConfirmationDialog,
    ShowDialogAsync,
    ShowErrorDialog,
    ShowSnackBarWithBlueCheckmark,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import { DeleteWorkspaceResourceFolderDialog } from "js/react/components/Dialogs/DeleteWorkspaceResourceFolderDialog";
import {
    WorkspaceDialog as ManageDialog,
    WorkspaceDialogBody as ManageDialogBody,
    WorkspaceDialogContent as ManageDialogContent,
    WorkspaceDialogField as FieldGroup,
    WorkspaceDialogFieldLabel as FieldLabel,
    WorkspaceDialogTextField as StyledTextField,
    WorkspaceDialogTitle as ManageDialogTitle
} from "js/react/components/Dialogs/WorkspaceDialogStyles";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { FolderAccessList } from "js/react/components/FolderAccessList";
import { createInviteSuggestionContext } from "js/react/components/InviteInput/InviteContext";
import { InviteTagsInput } from "js/react/components/InviteInput/InviteTagsInput";
import { IconMenu } from "js/react/components/UiComponents";
import { getFullUserDisplayName } from "js/react/views/UserOptions/panes/ManageUsersPane/Components/UsersList/UserListCommon";

const logger = getLogger();
const FOLDER_NAME_MAX_LENGTH = 50;

const TitleSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
`;

const Subtitle = styled.div`
    margin: 0;
    color: #666;
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%;
    letter-spacing: -0.32px;
`;

const AccessIconMenu = styled(IconMenu)`
    &&& {
        padding: 0;
    }
`;

const InviteRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    width: 100%;

    .invite-input,
    .react-tagsinput {
        flex: 1;
        min-width: 0;
    }
`;

const InviteButton = styled(BeautifulButton)`
    &&& {
        margin: 0;
        padding: 6px 16px;
        text-transform: uppercase;
        flex-shrink: 0;
    }
`;

const PermissionsCheckbox = styled(FormControlLabel)`
    &&& {
        align-self: flex-start;
        margin-left: 0;

        .MuiFormControlLabel-label {
            color: #666;
            font-family: "Source Sans Pro";
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: 130%;
        }
    }
`;

function isFolderNameTaken(name: string, folder: IWorkspaceResourceFolder, resourceFolders: IWorkspaceResourceFolder[]) {
    return resourceFolders.some(f =>
        f.id !== folder.id &&
        f.resourceTypes.some(resourceType => folder.resourceTypes.includes(resourceType)) &&
        f.folderType === folder.folderType &&
        f.parentFolderId === folder.parentFolderId &&
        f.name === name
    );
}

function buildFolderUserAddsFromSelection(
    selection: any[],
    members: IWorkspaceUserExtended[]
) {
    const add: { subjectId: string; subjectType: WorkspacePermissionSubjectType }[] = [];

    selection.forEach(item => {
        if (item.inviteError || item.type === "folder") {
            return;
        }

        if (item.type === "group") {
            add.push({
                subjectId: item.id,
                subjectType: WorkspacePermissionSubjectType.USER_GROUP
            });
            return;
        }

        const uid = item.uid || item.id;
        if (!uid || members.some(member => member.uid === uid)) {
            return;
        }

        add.push({
            subjectId: uid,
            subjectType: WorkspacePermissionSubjectType.USER
        });
    });

    return add;
}

function getDirectUsersWithAccessToFolder(folder: IWorkspaceResourceFolder, users: IWorkspaceUserExtended[]) {
    return users
        .filter(user => user.groupIds.includes(folder.userGroupId))
        .sort((a, b) =>
            getFullUserDisplayName(a.displayName, a.email)
                .localeCompare(getFullUserDisplayName(b.displayName, b.email))
        );
}

function getGroupsWithAccessToFolder(folder: IWorkspaceResourceFolder, userGroups: IWorkspaceUserGroup[]) {
    return userGroups
        .filter(group => group.id !== folder.userGroupId)
        .filter(group => group.groupIds.includes(folder.userGroupId))
        .filter(group => [WorkspaceUserGroupType.MEMBERS, WorkspaceUserGroupType.GUESTS, WorkspaceUserGroupType.CUSTOM].includes(group.type))
        .map(group => ({
            ...group,
            memberCount: WorkspaceController.getUsersInGroup(group.id).length
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

export function getUsersWithoutFolderAccess(
    users: IWorkspaceUserExtended[],
    usersWithAccess: IWorkspaceUserExtended[]
) {
    return users.filter(user => !usersWithAccess.some(member => member.id === user.id));
}

export function getBulkRemoveAccessSubjects(
    members: IWorkspaceUserExtended[],
    groupsWithAccess: IWorkspaceUserGroup[],
    currentUserUid?: string
) {
    return [
        ...members
            .filter(member => member.uid !== currentUserUid)
            .map(member => ({
                subjectId: member.uid,
                subjectType: WorkspacePermissionSubjectType.USER
            })),
        ...groupsWithAccess.map(group => ({
            subjectId: group.id,
            subjectType: WorkspacePermissionSubjectType.USER_GROUP
        }))
    ];
}

export function canBulkRemoveFolderAccessSubjects(
    members: IWorkspaceUserExtended[],
    currentUserUid?: string
) {
    return !!currentUserUid && members.some(member => member.uid === currentUserUid);
}

export function getFolderInviteSuggestionContextOptions(
    members: IWorkspaceUserExtended[],
    groupsWithAccess: IWorkspaceUserGroup[],
    saveContext: (inviteContext: any) => void
) {
    return {
        includeFolders: false,
        includeGroups: true,
        includeGroupTypes: [WorkspaceUserGroupType.GUESTS, WorkspaceUserGroupType.CUSTOM],
        allowEmailInvites: false,
        existingAccessItems: [...members, ...groupsWithAccess],
        oldSuggestions: [...members, ...groupsWithAccess],
        hideOld: true,
        saveContext
    };
}

export function shouldShowFolderAccessMenu({
    addableMemberCount,
    canBulkRemoveAccessSubjects,
    removableAccessSubjectCount
}: {
    addableMemberCount: number;
    canBulkRemoveAccessSubjects: boolean;
    removableAccessSubjectCount: number;
}) {
    return addableMemberCount > 0 || (canBulkRemoveAccessSubjects && removableAccessSubjectCount > 0);
}

export function isRemovingLastFolderAccessSubject(
    directMembers: IWorkspaceUserExtended[],
    groupsWithAccess: IWorkspaceUserGroup[]
) {
    return directMembers.length + groupsWithAccess.length === 1;
}

export function doesRemovingGroupRemoveUserAccess({
    currentUserUid,
    groupId,
    directMembers,
    groupsWithAccess,
    getGroupUserIds
}: {
    currentUserUid?: string;
    groupId: string;
    directMembers: IWorkspaceUserExtended[];
    groupsWithAccess: IWorkspaceUserGroup[];
    getGroupUserIds: (groupId: string) => string[];
}) {
    if (!currentUserUid || directMembers.some(member => member.uid === currentUserUid)) {
        return false;
    }

    const groupBeingRemovedProvidesAccess = getGroupUserIds(groupId).includes(currentUserUid);
    const anotherGroupProvidesAccess = groupsWithAccess
        .filter(group => group.id !== groupId)
        .some(group => getGroupUserIds(group.id).includes(currentUserUid));

    return groupBeingRemovedProvidesAccess && !anotherGroupProvidesAccess;
}

export interface ManageTeamFolderMembersDialogProps {
    folderId: string;
    closeDialog: () => void;
    viewOnly?: boolean;
}

export const ManageTeamFolderMembersDialog = WorkspaceController.withInitializedState(React.forwardRef(
    function ManageTeamFolderMembersDialog(props: ManageTeamFolderMembersDialogProps & WorkspaceControllerState, ref: React.Ref<any>) {
        const { folderId, resourceFolders, closeDialog, users, userGroups, viewOnly } = props;
        const folder = resourceFolders.find(f => f.id === folderId);
        if (!folder) {
            throw new Error("Folder not found");
        }

        const [fetching, setFetching] = useState(false);
        const [userPermissionsToFolder, setUserPermissionsToFolder] = useState<IWorkspacePermissionAccess>(GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS());
        const [onlyOwnerCanEditFetching, setOnlyOwnerCanEditFetching] = useState(false);
        const [folderName, setFolderName] = useState(folder.name);
        const [inviteContext, setInviteContext] = useState<any>(null);
        const [inviting, setInviting] = useState(false);

        useEffect(() => {
            setFolderName(folder.name);
        }, [folder.name]);

        useEffect(() => {
            (async () => {
                setFetching(true);
                try {
                    const userPermissions = await getUserPermissionsToResourceFolder({
                        workspaceId: folder.workspaceId,
                        folderId: folder.id
                    });
                    setUserPermissionsToFolder(userPermissions);
                    setFetching(false);
                } catch (err) {
                    logger.error(err, "[ManageTeamFolderMembersDialog] Failed to fetch folder permissions");
                }
            })();
        }, [getObjectHash(folder)]);

        const allTeamMembersFolder = isAllTeamMembersFolder(folder);
        const onlyOwnerCanEdit = !folder.permissionsOverrides?.[WorkspaceUserRole.MEMBER].write.permissions;
        const canManagePermissions = userPermissionsToFolder.write.permissions;
        const canShareTeamFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
        const canRenameFolder = (userPermissionsToFolder.write.object || userPermissionsToFolder.owner) && !viewOnly && !allTeamMembersFolder;
        const isFolderOwner = userPermissionsToFolder.owner;
        const canManageFolderAccess = canManagePermissions && (!allTeamMembersFolder || isFolderOwner);
        const canEdit = canManageFolderAccess && !viewOnly && canShareTeamFolders;
        const currentUserUid = auth().currentUser?.uid;

        const members = getDirectUsersWithAccessToFolder(folder, users);
        const groupsWithAccess = getGroupsWithAccessToFolder(folder, userGroups);
        const usersWithAccess = WorkspaceController.getUsersWithAccessToResourceFolder(folderId);
        const nonMembers = getUsersWithoutFolderAccess(users, usersWithAccess);
        const addableMemberCount = nonMembers.filter(member => member.role !== WorkspaceUserRole.GUEST).length;
        const canRemoveGroup = (group: IWorkspaceUserGroup) => !allTeamMembersFolder || isFolderOwner || group.type !== WorkspaceUserGroupType.MEMBERS;
        const removableAccessSubjects = getBulkRemoveAccessSubjects(members, groupsWithAccess.filter(canRemoveGroup), currentUserUid);
        const canBulkRemoveAccessSubjects = canBulkRemoveFolderAccessSubjects(members, currentUserUid);
        const showFolderAccessMenu = shouldShowFolderAccessMenu({
            addableMemberCount,
            canBulkRemoveAccessSubjects,
            removableAccessSubjectCount: removableAccessSubjects.length
        });

        useEffect(() => {
            if (!canEdit) {
                setInviteContext(null);
                return;
            }

            const context = createInviteSuggestionContext(getFolderInviteSuggestionContextOptions(
                members,
                groupsWithAccess,
                ic => setInviteContext(_.cloneDeep(ic))
            ));
            setInviteContext(context);

            return () => context.destroy();
        }, [canEdit, folderId, getObjectHash(groupsWithAccess), getObjectHash(members)]);

        const handleChangeOnlyOwnerCanEdit = async () => {
            try {
                setOnlyOwnerCanEditFetching(true);
                await WorkspaceController.updateWorkspaceResourceFolder({
                    folderId: folder.id,
                    permissionsOverrides: {
                        [WorkspaceUserRole.LIBRARIAN]: {
                            read: {
                                object: true,
                                permissions: true
                            },
                            write: {
                                object: true,
                                // Note: we're sending the flipped value here as we're
                                // handling checkbox toggling
                                permissions: onlyOwnerCanEdit
                            },
                            owner: false
                        },
                        [WorkspaceUserRole.MEMBER]: {
                            read: {
                                object: true,
                                permissions: true
                            },
                            write: {
                                object: true,
                                // Note: we're sending the flipped value here as we're
                                // handling checkbox toggling
                                permissions: onlyOwnerCanEdit
                            },
                            owner: false
                        }
                    }
                });
                setOnlyOwnerCanEditFetching(false);
            } catch (err) {
                logger.error(err, "[ManageTeamFolderMembersDialog] Failed to update only owner can edit");

                ShowErrorDialog({
                    title: t("Failed to update only owner can edit"),
                    message: t("Please try again later.")
                });
            }
        };

        const handleAddAllTeamMembers = async () => {
            try {
                await withProgressDialog({
                    title: t("Adding all team members"),
                    message: t("Please wait while we add all team members..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsersAndGroups({
                        folderId: folder.id,
                        update: {
                            add: nonMembers
                                .filter(member => member.role !== WorkspaceUserRole.GUEST)
                                .map(member => ({
                                    subjectId: member.uid,
                                    subjectType: WorkspacePermissionSubjectType.USER
                                }))
                        }
                    })
                });
            } catch (err) {
                logger.error(err, "[ManageTeamFolderMembersDialog] Failed to add all team members");

                ShowErrorDialog({
                    title: t("Failed to add all team members"),
                    message: t("Please try again later.")
                });
            }
        };

        const handleRemoveAllTeamMembers = async () => {
            try {
                const confirmed = await ShowConfirmationDialog({
                    title: t("Remove all members?"),
                    message: t("This will remove all members from this folder and revoke access to any presentations in it. You will continue to have access to the folder and its presentations."),
                    okButtonLabel: t("Remove Members"),
                    buttonOptions: { variant: "text", style: { color: "#FF4500" } },
                });
                if (!confirmed) {
                    return;
                }

                await withProgressDialog({
                    title: t("Removing all team members"),
                    message: t("Please wait while we remove all team members..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsersAndGroups({
                        folderId: folder.id,
                        update: {
                            remove: removableAccessSubjects
                        }
                    })
                });

                ShowSnackBarWithBlueCheckmark(t("All members removed"));
            } catch (err) {
                logger.error(err, "[ManageTeamFolderMembersDialog] Failed to remove all team members");

                ShowErrorDialog({
                    title: t("Failed to remove all team members"),
                    message: t("Please try again later.")
                });
            }
        };

        const handleRemovingLastAccessSubject = async () => {
            const deleteFolder = await ShowConfirmationDialog({
                title: t("You can’t remove the last member"),
                message: t("This would leave no one with access. Add another member or group, or delete the folder instead."),
                cancelButtonLabel: t("Cancel"),
                okButtonLabel: t("Delete Folder")
            });
            if (!deleteFolder) {
                return;
            }

            const deleted = await ShowDialogAsync(DeleteWorkspaceResourceFolderDialog, {
                folder,
                resourceFolders,
                resourceTypes: folder.resourceTypes
            });
            if (deleted) {
                closeDialog();
            }
        };

        const handleRemoveMember = async (uid: string) => {
            try {
                if (isRemovingLastFolderAccessSubject(members, groupsWithAccess)) {
                    await handleRemovingLastAccessSubject();
                    return;
                }

                const confirmed = await ShowConfirmationDialog({
                    title: t("Remove member?"),
                    message: t("This member will be removed from this folder and access to any presentations in it will be revoked."),
                    okButtonLabel: t("Remove Member"),
                    buttonOptions: { variant: "text", style: { color: "#FF4500" } },
                });
                if (!confirmed) {
                    return;
                }

                await withProgressDialog({
                    title: t("Removing team member"),
                    message: t("Please wait while we remove the team member..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsersAndGroups({
                        folderId: folder.id,
                        update: {
                            remove: [{
                                subjectId: uid,
                                subjectType: WorkspacePermissionSubjectType.USER
                            }]
                        }
                    })
                });

                ShowSnackBarWithBlueCheckmark(t("Member removed"));
            } catch (err) {
                logger.error(err, "[ManageTeamFolderMembersDialog] Failed to remove team member");

                ShowErrorDialog({
                    title: t("Failed to remove team member"),
                    message: t("Please try again later.")
                });
            }
        };

        const handleRemoveGroup = async (groupId: string) => {
            try {
                const group = userGroups.find(userGroup => userGroup.id === groupId);
                if (isRemovingLastFolderAccessSubject(members, groupsWithAccess)) {
                    await handleRemovingLastAccessSubject();
                    return;
                }

                const removesCurrentUserAccess = !isFolderOwner && doesRemovingGroupRemoveUserAccess({
                    currentUserUid,
                    groupId,
                    directMembers: members,
                    groupsWithAccess,
                    getGroupUserIds: id => WorkspaceController.getUsersInGroup(id).map(user => user.uid)
                });
                const confirmed = await ShowConfirmationDialog({
                    title: removesCurrentUserAccess ? t("Remove your access to this folder?") : t("Remove group?"),
                    message: removesCurrentUserAccess
                        ? (
                            <Trans
                                i18nKey="You access this folder through <strong>{{groupName}}</strong>. If you remove the group, you’ll no longer be able to access the folder or its contents."
                                values={{ groupName: group?.name || t("Group") }}
                                components={{ strong: <strong /> }}
                            />
                        )
                        : t("This group will be removed from this folder and access to any presentations in it will be revoked."),
                    okButtonLabel: t("Remove Group"),
                    buttonOptions: { variant: "text", style: { color: "#FF4500" } },
                });
                if (!confirmed) {
                    return;
                }

                if (removesCurrentUserAccess) {
                    closeDialog();
                }

                await withProgressDialog({
                    title: t("Removing group"),
                    message: t("Please wait while we remove the group..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsersAndGroups({
                        folderId: folder.id,
                        update: {
                            remove: [{
                                subjectId: groupId,
                                subjectType: WorkspacePermissionSubjectType.USER_GROUP
                            }]
                        }
                    })
                });

                ShowSnackBarWithBlueCheckmark(t("{{groupName}} removed", { groupName: group?.name || t("Group") }));
            } catch (err) {
                logger.error(err, "[ManageTeamFolderMembersDialog] Failed to remove group");

                ShowErrorDialog({
                    title: t("Failed to remove group"),
                    message: t("Please try again later.")
                });
            }
        };

        const handleRenameFolder = async () => {
            if (!canRenameFolder) {
                return;
            }

            const trimmedName = folderName.trim();
            if (!trimmedName || trimmedName === folder.name) {
                setFolderName(folder.name);
                return;
            }

            if (isFolderNameTaken(trimmedName, folder, resourceFolders)) {
                ShowWarningDialog({
                    title: t("Unable to rename folder"),
                    message: t("Please enter a unique folder name.")
                });
                setFolderName(folder.name);
                return;
            }

            try {
                await withProgressDialog({
                    title: t("Renaming folder..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolder({
                        folderId: folder.id,
                        name: trimmedName
                    })
                });
            } catch (err) {
                logger.error(err, "[ManageTeamFolderMembersDialog] Failed to rename folder");
                setFolderName(folder.name);
                ShowErrorDialog({
                    title: t("Failed to rename folder"),
                    message: t("An error occurred while renaming the folder. Please try again.")
                });
            }
        };

        const handleInvite = async () => {
            if (!inviteContext?.selection?.length || inviting) {
                return;
            }

            const add = buildFolderUserAddsFromSelection(
                inviteContext.selection,
                members
            );
            const isAddingGroup = add.length > 0 &&
                add.every(item => item.subjectType === WorkspacePermissionSubjectType.USER_GROUP);

            if (add.length === 0) {
                inviteContext.deselectItems?.(inviteContext.selection);
                return;
            }

            try {
                setInviting(true);
                await withProgressDialog({
                    title: isAddingGroup ? t("Adding group") : t("Adding team member"),
                    message: isAddingGroup
                        ? t("Please wait while we add the group...")
                        : t("Please wait while we add the team member..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsersAndGroups({
                        folderId: folder.id,
                        update: { add }
                    })
                });
                inviteContext.deselectItems?.(inviteContext.selection);
            } catch (err) {
                logger.error(err, "[ManageTeamFolderMembersDialog] Failed to invite members");
                ShowErrorDialog({
                    title: isAddingGroup ? t("Failed to add group") : t("Failed to add team member"),
                    message: t("Please try again later.")
                });
            } finally {
                setInviting(false);
            }
        };

        const showPermissionsCheckbox = isFolderOwner && !viewOnly && !allTeamMembersFolder;

        const title = canManageFolderAccess
            ? t("Manage Access and Settings")
            : formatter.capitalizeFirstLetter(folderName);

        const ownerWithAccess = usersWithAccess.find(user => user.role === WorkspaceUserRole.OWNER);
        const ownerName = ownerWithAccess?.displayName || ownerWithAccess?.email;

        const subTitle = canManageFolderAccess
            ? t("Control who on your team has access to this folder, its sub-folders, and presentations.")
            : ownerName
                ? t("View who on your team has access to this folder, its sub-folders, and presentations. Only the folder owner, {{ownerName}} can add or remove members.", { ownerName })
                : t("View who on your team has access to this folder, its sub-folders, and presentations.");

        return (
            <ManageDialog closeDialog={closeDialog} ref={ref} maxWidth="md" data-test-id="manage-team-folder-members-dialog">
                <FetchingClickShield visible={fetching || inviting} backgroundColor="white" />
                <ManageDialogBody data-test-id="manage-team-folder-members-dialog-content">
                    <TitleSection>
                        <ManageDialogTitle>
                            {title}
                        </ManageDialogTitle>
                        <Subtitle>
                            {subTitle}
                        </Subtitle>
                    </TitleSection>

                    <FieldGroup>
                        <FieldLabel>{t("Folder Name")}</FieldLabel>
                        <StyledTextField
                            value={folderName}
                            onChange={event => setFolderName(event.target.value)}
                            onBlur={handleRenameFolder}
                            onKeyPress={event => {
                                if (event.key === "Enter") {
                                    (event.target as HTMLInputElement).blur();
                                }
                            }}
                            variant="standard"
                            fullWidth
                            disabled={!canRenameFolder}
                            inputProps={{ maxLength: FOLDER_NAME_MAX_LENGTH }}
                        />
                    </FieldGroup>

                    {canEdit && inviteContext && (
                        <FieldGroup>
                            <FieldLabel>{t("Add Members")}</FieldLabel>
                            <InviteRow>
                                <InviteTagsInput
                                    inviteContext={inviteContext}
                                    sourceName="ManageTeamFolderMembersDialog"
                                />
                                <InviteButton
                                    large
                                    blue
                                    disabled={!inviteContext.selection?.some(item => !item.inviteError) || inviting}
                                    onClick={handleInvite}
                                >
                                    {t("Invite")}
                                </InviteButton>
                            </InviteRow>
                        </FieldGroup>
                    )}

                    <FolderAccessList
                        members={members}
                        groups={groupsWithAccess}
                        showYouLabel
                        onRemoveMember={canEdit ? handleRemoveMember : undefined}
                        onRemoveGroup={canEdit ? handleRemoveGroup : undefined}
                        canRemoveGroup={canRemoveGroup}
                        headerActions={canEdit && showFolderAccessMenu ? (
                            // @ts-ignore
                            <AccessIconMenu>
                                {addableMemberCount > 0 &&
                                <MenuItem onClick={handleAddAllTeamMembers}>
                                    <Icon>group_add</Icon>{t("Add all team members to folder")}
                                </MenuItem>
                                }
                                {canBulkRemoveAccessSubjects && removableAccessSubjects.length > 0 &&
                                <MenuItem onClick={handleRemoveAllTeamMembers}>
                                    <Icon>group_remove</Icon>{t("Remove all members")}
                                </MenuItem>
                                }
                            </AccessIconMenu>
                        ) : undefined}
                    />

                    {showPermissionsCheckbox && (
                        <PermissionsCheckbox
                            control={
                                <Checkbox
                                    color="primary"
                                    size="small"
                                    checked={!onlyOwnerCanEdit}
                                    onChange={handleChangeOnlyOwnerCanEdit}
                                    disabled={onlyOwnerCanEditFetching}
                                />
                            }
                            label={t("Allow members to manage folder permissions")}
                        />
                    )}
                </ManageDialogBody>
            </ManageDialog>
        );
    }
));
